<template>
  <v-row>
    <v-col cols="12">
      <!-- judul halaman + aksi -->
      <div class="bm-page-head">
        <div class="bm-title">Budget Monitoring</div>
        <div class="bm-subtitle">
          Belanja PO supplier + pemakaian stok, dibandingkan budget tiap PO customer.
        </div>
        <v-spacer></v-spacer>
        <v-btn
          outlined
          color="primary"
          :disabled="running"
          :loading="recomputing"
          @click="confirmRecompute = true"
        >
          <v-icon left>mdi-refresh</v-icon
          >{{ running ? "Sedang menghitung" : "Hitung ulang semua" }}
        </v-btn>
        <v-btn outlined color="primary" :to="'/setting/budget-monitoring'">
          <v-icon left>mdi-cog-outline</v-icon>Setting
        </v-btn>
      </div>

      <!-- ringkasan: status perhitungan + jumlah PO per band -->
      <budget-summary-strip
        :summary="summary"
        :last-run="lastRun"
        :active-band="filters.band"
        class="mb-4"
        @band="setBand"
      ></budget-summary-strip>

      <v-card>
        <v-tabs v-model="tab" background-color="transparent" color="primary">
          <v-tab>PO customer</v-tab>
          <v-tab>Job</v-tab>
          <v-spacer></v-spacer>
          <div class="bm-tiny align-self-center mr-5 bm-ell">
            {{
              tab === 0
                ? "Satu baris = satu PO customer. Band dan email dihitung per PO."
                : "Sudut pandang PPIC / PM. Budget job = bagian dari budget PO yang terhubung. Band informasi saja, tanpa email."
            }}
          </div>
        </v-tabs>
        <v-divider></v-divider>

        <!-- filter: baris 1 cakupan + cari, baris 2 tampilan -->
        <div class="bm-filters">
          <div class="bm-filter-row">
            <span class="bm-cap bm-filter-label">CAKUPAN</span>
            <template v-if="tab === 0">
              <v-select
                v-model="filters.year"
                :items="dropdownYear"
                item-text="name"
                item-value="year"
                label="Tahun PO"
                outlined
                dense
                hide-details
                class="bm-w-110"
                @change="reloadFromFirstPage"
              ></v-select>
              <v-text-field
                v-model="filters.customer"
                label="Customer"
                placeholder="Semua customer"
                outlined
                dense
                hide-details
                clearable
                class="bm-w-180"
                @change="reloadFromFirstPage"
              ></v-text-field>
              <v-text-field
                v-model="filters.sales"
                label="Sales"
                placeholder="Semua sales"
                outlined
                dense
                hide-details
                clearable
                class="bm-w-160"
                @change="reloadFromFirstPage"
              ></v-text-field>
            </template>
            <v-select
              v-else
              v-model="jobFilters.status"
              :items="jobStatusItems"
              label="Status job"
              outlined
              dense
              hide-details
              class="bm-w-180"
            ></v-select>
            <v-select
              v-model="filters.band"
              :items="bandItems"
              label="Band"
              outlined
              dense
              hide-details
              class="bm-w-140"
              @change="reloadFromFirstPage"
            ></v-select>
            <v-spacer></v-spacer>
            <v-text-field
              v-if="tab === 0"
              v-model="filters.q"
              append-icon="mdi-magnify"
              label="Cari nomor PO"
              placeholder="mis. 4100003000"
              outlined
              dense
              hide-details
              clearable
              class="bm-w-260"
              @keyup.enter="reloadFromFirstPage"
              @click:append="reloadFromFirstPage"
              @click:clear="clearSearch"
            ></v-text-field>
            <v-text-field
              v-else
              v-model="jobFilters.q"
              append-icon="mdi-magnify"
              label="Cari nomor job / nama"
              placeholder="mis. 1146998"
              outlined
              dense
              hide-details
              clearable
              class="bm-w-260"
            ></v-text-field>
          </div>
          <div class="bm-filter-row">
            <span class="bm-cap bm-filter-label">TAMPILKAN</span>
            <template v-if="tab === 0">
              <v-checkbox
                v-model="filters.includeClosed"
                label="PO tidak aktif"
                dense
                hide-details
                class="mt-0"
                @change="reloadFromFirstPage"
              ></v-checkbox>
              <v-checkbox
                v-model="filters.lowConfidence"
                label="Hanya PO dengan angka estimasi tinggi"
                dense
                hide-details
                class="mt-0"
                @change="reloadFromFirstPage"
              ></v-checkbox>
            </template>
            <template v-else>
              <v-checkbox
                v-model="jobFilters.noPo"
                label="Job tanpa PO CRM"
                dense
                hide-details
                class="mt-0"
              ></v-checkbox>
            </template>
            <v-spacer></v-spacer>
            <a class="bm-tiny" @click="resetFilters">Reset filter</a>
          </div>
        </div>
        <v-divider></v-divider>

        <v-tabs-items v-model="tab">
          <!-- ============================ TAB PO CUSTOMER ============================ -->
          <v-tab-item>
            <v-data-table
              :headers="posHeaders"
              :items="pos"
              :options.sync="options"
              :server-items-length="posTotal"
              :loading="loading"
              :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
              :item-class="rowClass"
              item-key="crmPoId"
              show-expand
              disable-sort
              hide-default-header
              dense
              class="bm-table"
            >
              <template v-slot:header="{ props }">
                <budget-grouped-header
                  :groups="posGroups"
                  :headers="props.headers"
                ></budget-grouped-header>
              </template>
              <template v-slot:item.poNumber="{ item }">
                <div class="bm-cell">
                  <router-link
                    :to="'/report/budget-monitoring/po/' + item.crmPoId"
                    class="font-weight-medium text-decoration-none"
                    :title="item.poNumber"
                    >{{ item.poNumber }}</router-link
                  >
                  <span class="bm-tiny" :title="item.title">
                    <template v-if="item.title && item.title !== item.poNumber"
                      >{{ item.title }} ·
                    </template>
                    {{ item.jobCount ? item.jobCount + " job" : "belum ada job" }}
                  </span>
                </div>
              </template>
              <template v-slot:item.customer="{ item }">
                <div class="bm-cell">
                  <span :title="item.customer">{{ item.customer || "—" }}</span>
                  <span class="bm-tiny" :title="item.sales">{{ item.sales || "—" }}</span>
                </div>
              </template>
              <template v-slot:item.budgetIdr="{ item }">
                <span v-if="item.band !== 'NO_BUDGET'" class="bm-num">{{
                  formatIdr(item.budgetIdr)
                }}</span>
                <v-chip v-else outlined color="primary" x-small @click.stop="openOverride(item)"
                  >Isi margin</v-chip
                >
              </template>
              <template v-slot:item.spendIdr="{ item }">
                <div class="bm-cell bm-cell--end">
                  <span class="bm-num">{{ formatIdr(item.spendIdr) }}</span>
                  <v-chip
                    x-small
                    color="blue lighten-5"
                    class="blue--text text--darken-2 bm-chip-link"
                    @click.stop="openBreakdown(item, 'SPEND')"
                    >{{ item.spendPoCount }} PO supplier</v-chip
                  >
                </div>
              </template>
              <template v-slot:item.stockIdr="{ item }">
                <div class="bm-cell bm-cell--end">
                  <span class="bm-num">{{ formatIdr(item.stockIdr) }}</span>
                  <v-chip
                    x-small
                    :color="item.stockMrCount ? 'indigo lighten-5' : 'grey lighten-3'"
                    :class="item.stockMrCount ? 'indigo--text bm-chip-link' : 'grey--text'"
                    @click.stop="item.stockMrCount && openBreakdown(item, 'STOCK')"
                    >{{ item.stockMrCount }} MR</v-chip
                  >
                </div>
              </template>
              <template v-slot:item.usageIdr="{ item }">
                <span class="bm-num font-weight-medium">{{ formatIdr(item.usageIdr) }}</span>
                <v-icon
                  v-if="item.fxMissing"
                  small
                  color="error"
                  class="ml-1"
                  title="kurs tidak ditemukan"
                  >mdi-currency-usd-off</v-icon
                >
              </template>
              <template v-slot:item.ratioPercent="{ item }">
                <ratio-cell
                  :ratio="item.ratioPercent"
                  :band="item.band"
                  :projected="item.projectedRatioPercent"
                  :projected-band="item.projectedBand"
                ></ratio-cell>
              </template>
              <template v-slot:item.band="{ item }">
                <budget-band-chip
                  :band="item.band"
                  :confidence="confidenceLabel(item)"
                  :note="item.band === 'NO_BUDGET' ? 'margin kosong' : ''"
                ></budget-band-chip>
              </template>
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="grey lighten-5 py-2 px-4">
                  <v-row dense align="center">
                    <v-col cols="12" md="9" class="caption">
                      <div>
                        <strong>Subtotal</strong> {{ item.currency }} {{ formatIdr(item.subtotal) }}
                        <span v-if="item.currency !== 'IDR'">
                          = Rp {{ formatIdr(item.subtotalIdr) }}</span
                        >
                        · <strong>Margin</strong> {{ formatPct(item.marginPercent) }} ({{
                          marginSourceLabel(item.marginSource)
                        }}) · <strong>Committed</strong> Rp {{ formatIdr(item.committedIdr) }}
                        <a
                          v-if="Number(item.committedIdr) > 0"
                          class="ml-1"
                          @click="openBreakdown(item, 'COMMITTED')"
                          >rincian</a
                        >
                      </div>
                      <div>
                        <strong>Job</strong> {{ item.jobIds || "—" }} · <strong>Dihitung</strong>
                        {{ formatDateTime(item.computedAt) }}
                        <span v-if="item.lastNotifiedBand">
                          · <strong>Email terakhir</strong> {{ bandName(item.lastNotifiedBand) }}
                          {{ formatDateTime(item.lastNotifiedAt) }}</span
                        >
                      </div>
                      <div v-if="Number(item.unlinkedItemShare) > 0">
                        <v-icon x-small color="warning">mdi-link-off</v-icon>
                        {{ formatPct(item.unlinkedItemShare * 100) }} nilai item PO belum terhubung
                        ke job
                      </div>
                    </v-col>
                    <v-col cols="12" md="3" class="text-right">
                      <v-btn
                        small
                        color="primary"
                        text
                        :to="'/report/budget-monitoring/po/' + item.crmPoId"
                      >
                        Buka detail<v-icon right small>mdi-arrow-right</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </td>
              </template>
              <template v-slot:no-data>
                <empty-state
                  v-if="!lastRun"
                  icon="mdi-refresh"
                  title="Belum ada perhitungan"
                  text="Perhitungan otomatis berjalan tiap jam. Untuk melihat data sekarang, jalankan sekali secara manual (± 30 detik)."
                >
                  <v-btn color="primary" class="mt-2" @click="confirmRecompute = true"
                    >Hitung ulang semua</v-btn
                  >
                </empty-state>
                <empty-state
                  v-else
                  icon="mdi-magnify-remove-outline"
                  title="Tidak ada PO yang cocok"
                  :text="activeFilterCopy"
                >
                  <v-btn outlined small color="primary" class="mt-2" @click="resetFilters"
                    >Reset filter</v-btn
                  >
                </empty-state>
              </template>
              <template v-slot:footer.prepend>
                <span class="bm-tiny d-flex align-center flex-wrap ml-2" style="gap: 14px">
                  <span><span class="bm-dot" style="background: #e53935"></span>&gt; 100 %</span>
                  <span><span class="bm-dot" style="background: #fb8c00"></span>80–100 %</span>
                  <span><span class="bm-dot" style="background: #4caf50"></span>≤ 80 %</span>
                  <span
                    ><v-icon x-small color="warning">mdi-approximately-equal</v-icon> estimasi = job
                    berbagi PO, alokasi proporsional</span
                  >
                  <span>→ akhir = proyeksi bila sisa BOM dibeli dengan harga terakhir</span>
                </span>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- ============================ TAB JOB ============================ -->
          <v-tab-item>
            <v-alert
              v-if="summary && Number(summary.noPoJobCount) > 0"
              type="warning"
              text
              dense
              tile
              class="mb-0"
              icon="mdi-link-variant-off"
            >
              <strong>{{ summary.noPoJobCount }} job tanpa PO CRM</strong> punya belanja dan
              pemakaian stok <strong>Rp {{ formatIdr(summary.noPoAmount) }}</strong> yang tidak
              masuk budget mana pun. Hubungkan job ke PO di PPIC agar terpantau.
              <a class="ml-2 font-weight-medium" @click="openNoPo">Lihat rinciannya</a>
            </v-alert>
            <v-data-table
              :headers="jobHeaders"
              :items="filteredJobs"
              :loading="loadingJobs"
              :items-per-page="25"
              :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1] }"
              :item-class="jobRowClass"
              item-key="jobId"
              disable-sort
              hide-default-header
              dense
              class="bm-table"
            >
              <template v-slot:header="{ props }">
                <budget-grouped-header
                  :groups="jobGroups"
                  :headers="props.headers"
                ></budget-grouped-header>
              </template>
              <template v-slot:item.jobName="{ item }">
                <div class="bm-cell">
                  <router-link
                    :to="'/report/budget-monitoring/job/' + item.jobId"
                    class="font-weight-medium text-decoration-none"
                    :title="item.jobName"
                    >{{ item.jobId }} · {{ item.jobName || "—" }}</router-link
                  >
                  <span class="bm-tiny" :title="item.customer">{{ item.customer || "—" }}</span>
                </div>
              </template>
              <template v-slot:item.poShares="{ item }">
                <div class="bm-cell">
                  <template v-if="(item.poShares || []).length">
                    <span>
                      <router-link
                        v-for="(s, i) in item.poShares"
                        :key="s.crmPoId"
                        :to="'/report/budget-monitoring/po/' + s.crmPoId"
                        class="text-decoration-none"
                        >{{ i ? ", " : "" }}{{ s.poNumber || "#" + s.crmPoId }}</router-link
                      >
                    </span>
                    <span class="bm-tiny">{{ shareCopy(item) }} · {{ statusCopy(item) }}</span>
                  </template>
                  <template v-else>
                    <v-chip
                      x-small
                      color="orange lighten-5"
                      class="orange--text text--darken-3 align-self-start"
                      >tanpa PO CRM</v-chip
                    >
                    <span class="bm-tiny">{{ statusCopy(item) }}</span>
                  </template>
                </div>
              </template>
              <template v-slot:item.budgetIdr="{ item }">
                <span v-if="(item.poShares || []).length" class="bm-num">{{
                  formatIdr(item.budgetIdr)
                }}</span>
                <span v-else class="grey--text">—</span>
              </template>
              <template v-slot:item.spendIdr="{ item }">
                <div class="bm-cell bm-cell--end">
                  <span class="bm-num">{{ formatIdr(item.spendIdr) }}</span>
                  <v-chip
                    x-small
                    :color="Number(item.spendIdr) ? 'blue lighten-5' : 'grey lighten-3'"
                    :class="
                      Number(item.spendIdr)
                        ? 'blue--text text--darken-2 bm-chip-link'
                        : 'grey--text'
                    "
                    @click.stop="Number(item.spendIdr) && openJobBreakdown(item, 'SPEND')"
                    >PO supplier</v-chip
                  >
                </div>
              </template>
              <template v-slot:item.stockIdr="{ item }">
                <div class="bm-cell bm-cell--end">
                  <span class="bm-num">{{ formatIdr(item.stockIdr) }}</span>
                  <v-chip
                    x-small
                    :color="Number(item.stockIdr) ? 'indigo lighten-5' : 'grey lighten-3'"
                    :class="Number(item.stockIdr) ? 'indigo--text bm-chip-link' : 'grey--text'"
                    @click.stop="Number(item.stockIdr) && openJobBreakdown(item, 'STOCK')"
                    >MR</v-chip
                  >
                </div>
              </template>
              <template v-slot:item.usageIdr="{ item }">
                <span class="bm-num font-weight-medium">{{ formatIdr(item.usageIdr) }}</span>
              </template>
              <template v-slot:item.ratioPercent="{ item }">
                <ratio-cell
                  :ratio="item.ratioPercent"
                  :band="item.bandInfo"
                  :projected="item.projectedRatioPercent"
                  :projected-band="item.projectedBand"
                ></ratio-cell>
              </template>
              <template v-slot:item.bandInfo="{ item }">
                <budget-band-chip
                  :band="item.bandInfo"
                  :confidence="jobConfidence(item)"
                  :note="(item.poShares || []).length ? '' : 'tidak masuk budget'"
                ></budget-band-chip>
              </template>
              <template v-slot:no-data>
                <empty-state
                  icon="mdi-magnify-remove-outline"
                  title="Tidak ada job yang cocok"
                  text="Ubah status job, band, atau kata kunci."
                >
                  <v-btn outlined small color="primary" class="mt-2" @click="resetFilters"
                    >Reset filter</v-btn
                  >
                </empty-state>
              </template>
            </v-data-table>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-col>

    <budget-spend-breakdown v-model="showBreakdown" :context="breakdown"></budget-spend-breakdown>
    <budget-override-dialog
      v-model="showOverride"
      :po="overridePo"
      @saved="afterOverride"
    ></budget-override-dialog>
    <budget-recompute-dialog
      v-model="confirmRecompute"
      scope="all"
      :subject="recomputeSubject"
      :last-run-at="lastRun ? formatDateTime(lastRun.finishedAt || lastRun.startedAt) : ''"
      :loading="recomputing"
      @confirm="recomputeAll"
    ></budget-recompute-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" bottom>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
          v-if="snackbar.action"
          text
          v-bind="attrs"
          @click="
            snackbar.action.fn();
            snackbar.show = false;
          "
          >{{ snackbar.action.text }}</v-btn
        >
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Tutup</v-btn>
      </template>
    </v-snackbar>
  </v-row>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import BudgetSpendBreakdown from "@/components/budget/BudgetSpendBreakdown.vue";
import BudgetSummaryStrip from "@/components/budget/BudgetSummaryStrip.vue";
import BudgetGroupedHeader from "@/components/budget/BudgetGroupedHeader.vue";
import BudgetBandChip from "@/components/budget/BudgetBandChip.vue";
import BudgetRecomputeDialog from "@/components/budget/BudgetRecomputeDialog.vue";
import BudgetOverrideDialog from "@/components/budget/BudgetOverrideDialog.vue";
import RatioCell from "@/components/budget/RatioCell.vue";
import EmptyState from "@/components/budget/EmptyState.vue";
import {
  BAND_FILTER_ITEMS,
  bandName,
  bandRowClass,
  confidenceLabel,
  formatDate,
  formatDateTime,
  formatIdr,
  formatPct,
  marginSourceLabel,
  shareOf,
} from "@/utils/budgetMonitor";
import "@/components/budget/budget-monitor.css";

const POLL_MS = 4000;

export default {
  name: "budget-monitoring",
  components: {
    BudgetSpendBreakdown,
    BudgetSummaryStrip,
    BudgetGroupedHeader,
    BudgetBandChip,
    BudgetRecomputeDialog,
    BudgetOverrideDialog,
    RatioCell,
    EmptyState,
  },
  data: () => ({
    tab: 0,
    loading: false,
    loadingJobs: false,
    recomputing: false,
    confirmRecompute: false,
    poll: null,
    filters: {
      year: 0,
      customer: "",
      sales: "",
      band: "",
      q: "",
      includeClosed: false,
      lowConfidence: false,
    },
    jobFilters: { status: "ACTIVE", q: "", noPo: true },
    options: { page: 1, itemsPerPage: 25 },
    bandItems: BAND_FILTER_ITEMS,
    jobStatusItems: [
      { text: "Aktif", value: "ACTIVE" },
      { text: "Closed (dalam tenggang)", value: "CLOSED" },
      { text: "Semua", value: "" },
    ],
    showBreakdown: false,
    breakdown: { bucket: "SPEND", expectedAmount: 0 },
    showOverride: false,
    overridePo: null,
    snackbar: { show: false, text: "", color: "primary", timeout: 6000, action: null },
    // fixed columns sum to 1006; Customer · sales takes the rest (140 at 1440 wide, ~80 at 1366)
    posGroups: [
      { text: "PO CUSTOMER", span: 3 },
      { text: "BUDGET", span: 1 },
      { text: "PEMAKAIAN", span: 3 },
      { text: "KESEHATAN", span: 2 },
    ],
    posHeaders: [
      { text: "", value: "data-table-expand", width: 32 },
      { text: "Nomor PO · judul", value: "poNumber", width: 186 },
      { text: "Customer · sales", value: "customer" },
      { text: "Budget (IDR)", value: "budgetIdr", align: "end", width: 120, class: "bm-group-l" },
      { text: "Belanja PO", value: "spendIdr", align: "end", width: 126, class: "bm-group-l" },
      { text: "Stok", value: "stockIdr", align: "end", width: 118 },
      { text: "Total", value: "usageIdr", align: "end", width: 118 },
      {
        text: "Rasio · proyeksi",
        value: "ratioPercent",
        align: "end",
        width: 116,
        class: "bm-group-l",
      },
      { text: "Band", value: "band", width: 130 },
    ],
    jobGroups: [
      { text: "JOB PPIC", span: 2 },
      { text: "BUDGET", span: 1 },
      { text: "PEMAKAIAN", span: 3 },
      { text: "KESEHATAN", span: 2 },
    ],
    jobHeaders: [
      { text: "Job · nama · customer", value: "jobName" },
      { text: "PO CRM · share · status", value: "poShares", width: 196 },
      { text: "Bagian budget", value: "budgetIdr", align: "end", width: 120, class: "bm-group-l" },
      { text: "Belanja PO", value: "spendIdr", align: "end", width: 126, class: "bm-group-l" },
      { text: "Stok", value: "stockIdr", align: "end", width: 130 },
      { text: "Total", value: "usageIdr", align: "end", width: 130 },
      {
        text: "Rasio · proyeksi",
        value: "ratioPercent",
        align: "end",
        width: 122,
        class: "bm-group-l",
      },
      { text: "Band", value: "bandInfo", width: 124 },
    ],
  }),
  computed: {
    ...mapState("budgetMonitor", ["pos", "posTotal", "jobs", "summary"]),
    ...mapGetters("budgetMonitor", ["lastRun"]),
    running() {
      return !!this.lastRun && this.lastRun.status === "RUNNING";
    },
    dropdownYear() {
      const year = new Date().getFullYear();
      const result = [{ name: "Semua", year: 0 }];
      for (let i = 0; i <= 3; i++) result.push({ name: "" + (year - i), year: year - i });
      return result;
    },
    recomputeSubject() {
      return this.summary
        ? `${this.summary.total} PO · ${this.summary.jobCount} job`
        : "semua PO dan job";
    },
    activeFilterCopy() {
      const f = this.filters;
      const parts = [];
      if (f.year) parts.push(`tahun ${f.year}`);
      if (f.customer) parts.push(`customer "${f.customer}"`);
      if (f.sales) parts.push(`sales "${f.sales}"`);
      if (f.band) parts.push(`band ${bandName(f.band).toLowerCase()}`);
      if (f.q) parts.push(`cari "${f.q}"`);
      if (f.lowConfidence) parts.push("hanya estimasi tinggi");
      const closed = f.includeClosed ? "" : " PO tidak aktif disembunyikan.";
      return (
        (parts.length ? "Filter aktif: " + parts.join(" · ") + "." : "Tidak ada filter aktif.") +
        closed
      );
    },
    filteredJobs() {
      const q = (this.jobFilters.q || "").toLowerCase();
      const band = this.filters.band;
      const status = this.jobFilters.status;
      return (this.jobs || []).filter(j => {
        if (band && j.bandInfo !== band) return false;
        if (status === "ACTIVE" && j.closed) return false;
        if (status === "CLOSED" && !j.closed) return false;
        if (!this.jobFilters.noPo && !(j.poShares || []).length) return false;
        if (!q) return true;
        return (
          (j.jobName || "").toLowerCase().includes(q) ||
          (j.customer || "").toLowerCase().includes(q) ||
          String(j.jobId).includes(q)
        );
      });
    },
  },
  watch: {
    options: {
      handler() {
        this.loadPos();
      },
      deep: true,
    },
    tab(v) {
      if (v === 1 && !this.jobs.length) this.loadJobs();
    },
    running(v, old) {
      if (v) this.startPolling();
      else if (old) this.finishedRun();
    },
  },
  created() {
    this.loadSummary();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    formatIdr,
    formatPct,
    formatDate,
    formatDateTime,
    bandName,
    marginSourceLabel,
    confidenceLabel,
    rowClass(item) {
      return bandRowClass(item.band);
    },
    jobRowClass(item) {
      return bandRowClass(item.bandInfo);
    },
    shareCopy(item) {
      const shares = item.poShares || [];
      if (!shares.length) return "";
      const first = shareOf(shares[0], null);
      if (shares.length === 1)
        return first !== null ? `share ${Math.round(first * 100)} %` : "share —";
      return `${shares.length} PO`;
    },
    statusCopy(item) {
      if (!item.closed) return "aktif";
      return item.closingDate
        ? `closed ${formatDate(item.closingDate)} · dalam tenggang`
        : "closed · dalam tenggang";
    },
    jobConfidence(item) {
      const shares = item.poShares || [];
      if (!shares.length) return "";
      const est = shares.some(s => {
        const sh = shareOf(s, null);
        return sh !== null && sh < 1;
      });
      return est ? "estimasi" : "pasti";
    },
    setBand(band) {
      this.filters.band = band;
      this.reloadFromFirstPage();
    },
    clearSearch() {
      this.filters.q = "";
      this.reloadFromFirstPage();
    },
    resetFilters() {
      this.filters = {
        year: 0,
        customer: "",
        sales: "",
        band: "",
        q: "",
        includeClosed: false,
        lowConfidence: false,
      };
      this.jobFilters = { status: "ACTIVE", q: "", noPo: true };
      this.reloadFromFirstPage();
    },
    reloadFromFirstPage() {
      if (this.options.page !== 1) {
        this.options.page = 1; // watcher reloads
      } else {
        this.loadPos();
      }
      this.loadSummary();
    },
    listParams() {
      const f = this.filters;
      return {
        year: f.year || undefined,
        customer: f.customer || undefined,
        sales: f.sales || undefined,
        q: f.q || undefined,
        includeClosed: f.includeClosed,
        lowConfidence: f.lowConfidence,
      };
    },
    async loadPos() {
      this.loading = true;
      try {
        await this.$store.dispatch("budgetMonitor/fetchPos", {
          ...this.listParams(),
          band: this.filters.band || undefined,
          page: this.options.page - 1,
          size: this.options.itemsPerPage > 0 ? this.options.itemsPerPage : 100,
        });
      } catch (e) {
        this.$store.dispatch("toast", { type: "error", message: "Gagal memuat daftar PO" });
      } finally {
        this.loading = false;
      }
    },
    async loadSummary() {
      try {
        await this.$store.dispatch("budgetMonitor/fetchSummary", this.listParams());
      } catch (e) {
        this.$store.dispatch("budgetMonitor/fetchRuns").catch(() => {});
      }
    },
    async loadJobs() {
      this.loadingJobs = true;
      try {
        await this.$store.dispatch("budgetMonitor/fetchJobs", {});
      } catch (e) {
        this.$store.dispatch("toast", { type: "error", message: "Gagal memuat daftar job" });
      } finally {
        this.loadingJobs = false;
      }
    },
    openBreakdown(row, bucket) {
      this.breakdown = {
        crmPoId: row.crmPoId,
        bucket,
        expectedAmount:
          bucket === "STOCK"
            ? row.stockIdr
            : bucket === "COMMITTED"
            ? row.committedIdr
            : row.spendIdr,
        poNumber: row.poNumber,
        estimatedPercent: row.estimatedPercent,
        budgetIdr: row.budgetIdr,
        usageIdr: row.usageIdr,
      };
      this.showBreakdown = true;
    },
    openJobBreakdown(row, bucket) {
      this.breakdown = {
        jobId: row.jobId,
        jobName: row.jobName || "",
        bucket,
        expectedAmount: bucket === "STOCK" ? row.stockIdr : row.spendIdr,
      };
      this.showBreakdown = true;
    },
    openNoPo() {
      this.breakdown = {
        noPo: true,
        bucket: "SPEND",
        expectedAmount: this.summary ? this.summary.noPoAmount : 0,
        jobs: (this.jobs || []).map(j => ({ jobId: j.jobId, jobName: j.jobName })),
      };
      this.showBreakdown = true;
    },
    openOverride(row) {
      this.overridePo = row;
      this.showOverride = true;
    },
    afterOverride() {
      this.loadPos();
      this.loadSummary();
    },
    async recomputeAll() {
      this.recomputing = true;
      try {
        const res = await this.$store.dispatch("budgetMonitor/recomputeAll");
        this.confirmRecompute = false;
        if (res && res.started) {
          await this.loadSummary();
          if (!this.running) this.startPolling();
        } else {
          this.notify("Perhitungan lain sedang berjalan — tunggu selesai.", "info");
          this.startPolling();
        }
      } catch (e) {
        const status = e && e.response && e.response.status;
        this.confirmRecompute = false;
        if (status === 409) {
          this.notify(
            "Perhitungan otomatis sedang berjalan — tunggu selesai, hasilnya sama.",
            "info"
          );
          this.startPolling();
        } else {
          this.notify("Gagal memulai perhitungan. Coba lagi.", "error", 0, {
            text: "Coba lagi",
            fn: () => (this.confirmRecompute = true),
          });
        }
      } finally {
        this.recomputing = false;
      }
    },
    startPolling() {
      if (this.poll) return;
      this.poll = setInterval(() => {
        this.$store.dispatch("budgetMonitor/fetchRuns").catch(() => {});
      }, POLL_MS);
    },
    stopPolling() {
      if (this.poll) clearInterval(this.poll);
      this.poll = null;
    },
    async finishedRun() {
      this.stopPolling();
      await this.loadSummary();
      this.loadPos();
      if (this.tab === 1 || this.jobs.length) this.loadJobs();
      const r = this.lastRun;
      if (!r) return;
      if (r.status === "FAILED") {
        this.notify(
          `Perhitungan gagal: ${(r.errorSummary || "").split("\n")[0] ||
            "lihat log"}. Data sebelumnya tetap dipakai.`,
          "error",
          0,
          {
            text: "Coba lagi",
            fn: () => (this.confirmRecompute = true),
          }
        );
      } else {
        this.notify(
          `Perhitungan selesai · ${r.poCount || 0} PO · ${r.jobCount || 0} job${
            r.status === "PARTIAL" ? " · ada job/PO dilewati" : ""
          }`,
          r.status === "PARTIAL" ? "warning" : "success"
        );
      }
    },
    notify(text, color, timeout = 6000, action = null) {
      this.snackbar = { show: true, text, color, timeout: timeout === 0 ? -1 : timeout, action };
    },
  },
};
</script>

<style scoped>
.bm-filters {
  padding: 20px 20px 12px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.bm-filter-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 16px;
}
.bm-filter-label {
  width: 76px;
}
.bm-w-110 {
  max-width: 110px;
}
.bm-w-140 {
  max-width: 140px;
}
.bm-w-160 {
  max-width: 160px;
}
.bm-w-180 {
  max-width: 180px;
}
.bm-w-260 {
  max-width: 260px;
}
.bm-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}
</style>
